/**
 * Analytics helpers for Chiefy.AI (GA4)
 *
 * Rules:
 * - Never fires until the user grants cookie consent ('chiefy_consent' = 'granted').
 * - The GA4 gtag script is loaded lazily on first consent so it is never sent
 *   before the user accepts.
 * - All helpers are no-ops when GA_ID is absent (dev / test environments).
 */

import { captureError } from './core/errors/captureError';

const GA_ID = import.meta.env.VITE_GA_ID as string | undefined;
const CONSENT_KEY = 'chiefy_consent';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    _gaLoaded?: boolean;
  }
}

function isConsentGranted(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'granted';
  } catch (err) {
    captureError(err, { scope: 'analytics', op: 'isConsentGranted' });
    return false;
  }
}

function loadGtag(): void {
  if (!GA_ID || window._gaLoaded) return;
  window._gaLoaded = true;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { anonymize_ip: true });

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head.appendChild(script);
}

/** Call once on app boot — loads GA4 if consent was previously granted. */
export function initAnalytics(): void {
  if (!GA_ID) return;
  if (isConsentGranted()) {
    loadGtag();
  }
  // Listen for consent granted during this session
  window.addEventListener('chiefy:consent-granted', () => loadGtag(), { once: true });
}

/** Fire this when the user clicks Accept in the cookie banner. */
export function onConsentGranted(): void {
  try {
    localStorage.setItem(CONSENT_KEY, 'granted');
  } catch (err) {
    captureError(err, { scope: 'analytics', op: 'onConsentGranted' });
  }
  window.dispatchEvent(new Event('chiefy:consent-granted'));
  loadGtag();
}

function gtag(...args: unknown[]): void {
  if (!GA_ID || !window.gtag || !isConsentGranted()) return;
  window.gtag(...args);
}

// ── Typed event helpers ──────────────────────────────────────────────────────

export function trackPageView(path: string): void {
  gtag('event', 'page_view', { page_path: path });
}

export function trackTabChange(tab: string): void {
  gtag('event', 'tab_change', { event_category: 'navigation', event_label: tab });
}

export function trackChatStart(): void {
  // eslint-disable-next-line chiefy/no-naked-concept-strings -- GA event_category is an analytics infrastructure id, not user copy.
  gtag('event', 'chat_start', { event_category: 'engagement' });
}

export function trackUpgradeClick(source: string): void {
  gtag('event', 'upgrade_click', { event_category: 'monetisation', event_label: source });
}

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  gtag('event', name, params);
}

// ── Action-level chat events ─────────────────────────────────────────────
// Granular events the principal-level audit (§11 Tier-3) called out:
// we already track page_view + tab_change, but the critical funnel
// (send → regenerate → stop → upgrade) was invisible. Fire these from
// the useStreamingChat handlers + ChatRoom controls.

export function trackChatSend(params: { chatId?: string; length?: number }): void {
  gtag('event', 'chat_send', { event_category: 'assistant', ...params });
}

export function trackChatStop(): void {
  gtag('event', 'chat_stop', { event_category: 'assistant' });
}

export function trackChatRegenerate(): void {
  gtag('event', 'chat_regenerate', { event_category: 'assistant' });
}

export function trackChatRetry(): void {
  gtag('event', 'chat_retry', { event_category: 'assistant' });
}

export function trackQuotaWarning(fraction: number): void {
  gtag('event', 'quota_warning', {
    event_category: 'monetisation',
    event_label: `${Math.round(fraction * 100)}%`,
  });
}

export function trackQuotaExhausted(): void {
  gtag('event', 'quota_exhausted', { event_category: 'monetisation' });
}

export function trackSubmissionCreated(module: string): void {
  gtag('event', 'submission_created', {
    event_category: 'coaching',
    event_label: module,
  });
}

export function trackMentorReviewOpened(): void {
  gtag('event', 'mentor_review_opened', { event_category: 'coaching' });
}

export function trackConnectionAccepted(): void {
  gtag('event', 'connection_accepted', { event_category: 'networking' });
}
